<script setup>
import { toast } from 'dashboard/composables/useToast';
import MacrosTableRow from './MacrosTableRow.vue';
import SettingsPageLayout from 'dashboard/components-next/Settings/SettingsPageLayout.vue';
import SettingsTable from 'dashboard/components-next/Settings/SettingsTable.vue';
import SettingsListToolbar from 'dashboard/components-next/Settings/SettingsListToolbar.vue';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const showDeleteConfirmationPopup = ref(false);
const selectedMacro = ref({});

const records = computed(() => getters['macros/getMacros'].value);
const uiFlags = computed(() => getters['macros/getUIFlags'].value);

const searchQuery = ref('');
const filteredRecords = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  if (!q) return records.value;
  return records.value.filter(m => m.name?.toLowerCase().includes(q));
});

const deleteMessage = computed(() => ` ${selectedMacro.value.name}?`);

onMounted(() => {
  store.dispatch('macros/get');
});

const deleteMacro = async id => {
  try {
    await store.dispatch('macros/delete', id);
    toast.success(t('MACROS.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    toast.error(t('MACROS.DELETE.API.ERROR_MESSAGE'));
  }
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  selectedMacro.value = response;
};

const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const confirmDeletion = () => {
  closeDeletePopup();
  deleteMacro(selectedMacro.value.id);
};

const tableHeaders = computed(() => {
  return [
    t('MACROS.LIST.TABLE_HEADER.NAME'),
    t('MACROS.LIST.TABLE_HEADER.CREATED BY'),
    t('MACROS.LIST.TABLE_HEADER.LAST_UPDATED_BY'),
    t('MACROS.LIST.TABLE_HEADER.VISIBILITY'),
    '',
  ];
});
</script>

<template>
  <SettingsPageLayout
    :title="$t('MACROS.HEADER')"
    :description="$t('MACROS.DESCRIPTION')"
  >
    <template #actions>
      <router-link :to="{ name: 'macros_new' }">
        <Button
          icon="i-lucide-circle-plus"
          :label="$t('MACROS.HEADER_BTN_TXT')"
        />
      </router-link>
    </template>

    <SettingsListToolbar
      v-if="records.length"
      v-model="searchQuery"
      :placeholder="$t('MACROS.SEARCH_PLACEHOLDER')"
      :count-label="$t('MACROS.COUNT_LABEL', { count: filteredRecords.length })"
    />

    <SettingsTable
      :headers="tableHeaders"
      :is-loading="uiFlags.isFetching"
      :is-empty="!filteredRecords.length"
      :empty-message="
        records.length ? $t('MACROS.SEARCH_404') : $t('MACROS.LIST.404')
      "
    >
      <MacrosTableRow
        v-for="(macro, index) in filteredRecords"
        :key="index"
        :macro="macro"
        @delete="openDeletePopup(macro)"
      />
    </SettingsTable>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('LABEL_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('MACROS.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="$t('MACROS.DELETE.CONFIRM.YES')"
      :reject-text="$t('MACROS.DELETE.CONFIRM.NO')"
    />
  </SettingsPageLayout>
</template>
